import { Box } from '@components/basics/container';
import { IBox } from '@components/basics/container/Box/IBox';
import React, { useEffect, useRef, useState, useContext } from 'react';
import { ThemeContext } from 'styled-components';
import { Icons } from '@components/themes/default';
import { Icon } from '@components/basics/icon';
import { scrollIntoView } from 'seamless-scroll-polyfill';
import SharingOption from './SharingOption';

interface ISharingMenu extends IBox.BaseProps {
	sharingUrl?: string;
	sharingTitle?: string;
	sharingDescription?: string;
}

const SharingMenuArticle: React.FC<ISharingMenu> = ({ sharingUrl, sharingTitle, sharingDescription }): JSX.Element => {
	// Observer offset
	const offsetUpperElement = window?.app?.voiceReaderLink?.offset ? window.app.voiceReaderLink.offset + 12 : 36 - 8;

	const themeContext = useContext(ThemeContext);

	const upperElementRef = useRef(null);
	const lowerElementRef = useRef(null);
	const ctaWrapperElementRef = useRef(null);
	const commentsElementRef = useRef(null);

	const [menuIsFixed, setMenuIsFixed] = useState(false);
	const [isVoiceReaderDockedInitially, setVoiceReaderDockedInitially] = useState(false);
	const isVoiceReaderDocked =
		isVoiceReaderDockedInitially ||
		(window?.app?.voiceReaderLink?.node?.classList?.contains('voiceReaderButtonContainer--sticky') && menuIsFixed);
	const [menuIsVisible, setMenuIsVisible] = useState(true);
	const [navigatorCanShareNatively, setNavigatorCanShareNatively] = useState(false);
	const [hasCommentsSection, setHasCommentsSection] = useState(false);

	const shareData = {
		url: sharingUrl,
		title: sharingTitle,
		text: sharingDescription
	};

	// Upper reference (= article header)
	const handleIntersection1 = (entries: IntersectionObserverEntry[]) => {
		entries.forEach((element: IntersectionObserverEntry) => {
			// Element is APPEARING
			// ALWAYS set the sharing menu to absolute positioning
			if (element.isIntersecting === true) {
				setMenuIsFixed(false);
			}

			// Element is DISAPPEARING
			if (element.isIntersecting === false) {
				if (element.boundingClientRect.bottom <= offsetUpperElement) {
					// Disappearing from TOP of viewport
					setMenuIsFixed(true);
				} else {
					// Disappearing from BOTTOM of viewport
					setMenuIsFixed(false);
				}
			}
		});
	};

	// Lower reference (= default sharing menu)
	const handleIntersection2 = (entries: IntersectionObserverEntry[]) => {
		entries.forEach((element: IntersectionObserverEntry) => {
			// Element is APPEARING
			// ALWAYS hide the sharing menu
			if (element.isIntersecting === true) {
				setMenuIsVisible(false);
				window?.app?.voiceReaderLink?.node?.classList.add('voiceReaderButtonContainer--hidden');
			}

			// Element is DISAPPEARING
			if (element.isIntersecting === false) {
				if (element.boundingClientRect.top <= 0) {
					// Disappearing from TOP of viewport
					setMenuIsVisible(false);
					window?.app?.voiceReaderLink?.node?.classList.add('voiceReaderButtonContainer--hidden');
				} else {
					// Disappearing from BOTTOM of viewport
					setMenuIsVisible(true);
					window?.app?.voiceReaderLink?.node?.classList.remove('voiceReaderButtonContainer--hidden');
				}
			}
		});
	};

	const scrollToComments = () => {
		if (commentsElementRef.current) scrollIntoView(commentsElementRef.current, { behavior: 'smooth' });
	};

	const nativeShare = () => {
		navigator.share(shareData);
	};

	// This hook will be executed once when the component gets mounted
	useEffect(() => {
		// Get upper & lower "barrier" elements:
		// Roughly speaking: when scrolling in between these elements, the sharing menu button will get a 'fixed' position
		if (!upperElementRef.current) upperElementRef.current = document.querySelector('.con.conDetailHeader');
		if (!lowerElementRef.current) lowerElementRef.current = document.querySelector('.sharing-menu-default');
		if (!ctaWrapperElementRef.current) ctaWrapperElementRef.current = document.querySelector('#cta-wrapper');

		// Initial check if the voice reader button is positioned at the bottom of the article header (= there is no 'Einleitung')
		// In this case, the voice reader button + the sharing menu are 'docked' constantly
		const bottomArticleHeader = upperElementRef.current.getBoundingClientRect().bottom;
		const bottomCtaWrapper = ctaWrapperElementRef.current.getBoundingClientRect().bottom;

		if (bottomArticleHeader === bottomCtaWrapper + 12) {
			setVoiceReaderDockedInitially(true);
		}

		// Get comments section (if available):
		if (!commentsElementRef.current) commentsElementRef.current = document.querySelector('.con.conComments');
		if (commentsElementRef.current) setHasCommentsSection(true);

		// Create intersection observers:
		// We need to create 2 different observers because they need to have a slightly different behaviour (margin & threshold)
		const observer1 = new IntersectionObserver(handleIntersection1, {
			root: null, // = browser viewport
			rootMargin: `-${offsetUpperElement.toString()}px`,
			threshold: 0 // = trigger as soon as a single pixel is visible
		});
		const observer2 = new IntersectionObserver(handleIntersection2, {
			root: null, // = browser viewport
			// Trigger as soon as the element is entirely visible.
			// The value had to be changed from 1 to 0.9 because in Edge on Windows intersectionRatio is not exactly 1 but rather ~0.99.
			threshold: 0.9
		});

		// Observe "barrier" elements:
		// If elements are not found in DOM: do nothing (= absolute positioning of menu buttons)
		if (upperElementRef.current && lowerElementRef.current) {
			observer1.observe(upperElementRef.current);
			observer2.observe(lowerElementRef.current);
		}

		// Check whether web share API is available in current browser
		// and data is valid and permission has been granted
		navigator?.canShare && navigator.canShare(shareData) && setNavigatorCanShareNatively(true);

		// Cleanup before unmounting the component
		return () => {
			if (observer1?.disconnect) observer1.disconnect();
			if (observer2?.disconnect) observer2.disconnect();
		};
	}, []);

	return (
		<Box
			as="menu"
			position={menuIsFixed ? 'fixed' : 'absolute'}
			right={menuIsFixed ? 'calc((100vw - 1008px) * 0.5 - (100vw - 100%) * 0.5)' : '0'}
			top={menuIsFixed ? `calc(${offsetUpperElement.toString()}px - 0.25rem)` : 'unset'}
			bottom={menuIsFixed ? 'unset' : '0.25rem'}
			width="9.25rem"
			padding="0"
			display="none"
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			transform={menuIsFixed ? 'translateY(0%)' : 'translateY(100%)'}
			opacity={menuIsVisible ? '100%' : '0%'}
			zIndex={menuIsVisible ? '99' : '-1'}
			pointerEvents={menuIsVisible ? 'auto' : 'none'}
			lStyle={{ display: 'flex' }}
			customCss={{
				'list-style': 'none',
				transition: `${menuIsVisible ? 'opacity .2s ease-in, z-index 0s 0s' : 'opacity .2s ease-in, z-index 0s 0.2s'}`
			}}
			beforeStyle={{
				display: 'block',
				height: '1px',
				width: 'calc(100% - 10px)',
				margin: '0 auto',
				background: `${isVoiceReaderDocked ? themeContext.sharingMenu.menu.dividerColor : 'transparent'}`,
				position: 'absolute',
				left: '5px',
				top: '0'
			}}
		>
			{/* Teilen */}
			<Box
				as="li"
				width="100%"
				display="inline-flex"
				position="relative"
				customCss={{
					':focus-within > menu': {
						opacity: '1',
						zIndex: '10',
						transition: 'opacity .2s ease-in',
						// re-enable pointer events so the options are clickable
						pointerEvents: 'auto'
					}
				}}
			>
				<Box
					as="button"
					margin="0.5rem 0"
					width="100%"
					tabIndex={0 as never}
					display="inline-flex"
					alignItems="center"
					justifyContent="flex-start"
					cursor="pointer"
					padding="0.5rem"
					backgroundColor={themeContext.sharingMenu.trigger.button.backgroundColor}
					paddingTop="calc(0.5rem + 1px)"
					customCss={{
						':hover': { backgroundColor: themeContext.sharingMenu.trigger.button.backgroundColorHover },
						':focus': { textDecoration: 'none' },
						':focus-visible': {
							backgroundColor: themeContext.sharingMenu.trigger.button.backgroundColorHover,
							outline: 'none'
						},
						// we need to create a pseudo element for the backdrop effect
						// as Chrome prevents nested backdrop filters from stacking
						':before': {
							content: '""',
							display: 'block',
							position: 'absolute',
							left: '0',
							top: '0',
							width: '100%',
							height: 'calc(100% + 1rem)',
							background: 'rgba(255, 255, 255, 0.75)',
							backdropFilter: 'blur(4px)',
							zIndex: '-1',
							margin: '-0.5rem 0'
						}
					}}
					aria-label="Teilen"
					title="Teilen"
					position="relative"
					onClick={() => {
						navigatorCanShareNatively && nativeShare();
					}}
				>
					<Icon
						width="1.5rem"
						height="1.5rem"
						fontSize={'1.125rem'}
						color={themeContext.sharingMenu.trigger.button.iconColor}
						code={Icons.shareBoxOutline}
						marginRight="0.38rem"
					/>
					<Box
						color={themeContext.sharingMenu.trigger.button.textColor}
						textTransform="uppercase"
						fontSize="0.6875rem"
						fontStyle="normal"
						fontWeight="700"
						lineHeight="0.6875rem"
						letterSpacing="0.08313rem"
					>
						Teilen
					</Box>
				</Box>
				{/* menu: show when native sharing option is not available */}
				{!navigatorCanShareNatively && (
					<Box
						as="menu"
						display="grid"
						gridTemplateColumns="1fr"
						cursor="default"
						transition="opacity .2s ease-in" // animation: out
						opacity="0"
						zIndex="-1"
						borderRadius="0.125rem"
						backgroundColor={themeContext.sharingMenu.menu.backgroundColor}
						position="absolute"
						top="0"
						left="0"
						width="9.25rem"
						transform="translateX(calc(-100% - 1rem))"
						boxShadow="0px 2px 6px 0px rgba(0, 0, 0, 0.25)"
						customCss={{
							'backdrop-filter': 'blur(4px)',
							'list-style': 'none',
							li: {
								'border-bottom': `1px solid ${themeContext.sharingMenu.menu.dividerColor}`,
								'&:last-of-type': {
									'border-bottom': '0 none'
								}
							},
							'li a': {
								justifyContent: 'flex-start'
							},
							'li a .label': {
								fontSize: '0.6875rem',
								lineHeight: '0.875rem'
							}
						}}
						padding="0 0.5rem"
						// disable pointer events to ensure that invisible menu does not capture clicks,
						// will be re-enabled when the parent menu has focus within.
						pointerEvents="none"
					>
						<SharingOption
							sharingUrl={sharingUrl}
							sharingTitle={sharingTitle}
							sharingDescription={sharingDescription}
							type="facebook"
						/>
						<SharingOption
							sharingUrl={sharingUrl}
							sharingTitle={sharingTitle}
							sharingDescription={sharingDescription}
							type="twitter"
						/>
						<SharingOption
							sharingUrl={sharingUrl}
							sharingTitle={sharingTitle}
							sharingDescription={sharingDescription}
							type="whatsapp"
						/>
						<SharingOption
							sharingUrl={sharingUrl}
							sharingTitle={sharingTitle}
							sharingDescription={sharingDescription}
							type="mail"
						/>
						<SharingOption
							sharingUrl={sharingUrl}
							sharingTitle={sharingTitle}
							sharingDescription={sharingDescription}
							type="print"
						/>
					</Box>
				)}
			</Box>
			{/* Kommentare */}
			{hasCommentsSection && (
				<Box
					as="li"
					width="100%"
					cursor="pointer"
					display="block"
					position="relative"
					beforeStyle={{
						display: 'block',
						height: '1px',
						width: 'calc(100% - 10px)',
						margin: '0 auto',
						background: `${themeContext.sharingMenu.menu.dividerColor}`,
						position: 'absolute',
						left: '5px',
						top: '0'
					}}
				>
					<Box
						as="a"
						margin="0.5rem 0"
						width="100%"
						role="button"
						tabIndex={0 as never}
						display="inline-flex"
						alignItems="center"
						justifyContent="flex-start"
						cursor="pointer"
						padding="0.5rem"
						paddingTop="calc(0.5rem + 1px)"
						backgroundColor={themeContext.sharingMenu.trigger.button.backgroundColor}
						customCss={{
							':hover': { backgroundColor: themeContext.sharingMenu.trigger.button.backgroundColorHover },
							':focus': { textDecoration: 'none' },
							':focus-visible': {
								backgroundColor: themeContext.sharingMenu.trigger.button.backgroundColorHover,
								outline: 'none'
							},
							// we need to create a pseudo element for the backdrop effect
							// as Chrome prevents nested backdrop filters from stacking
							':before': {
								content: '""',
								display: 'block',
								position: 'absolute',
								left: '0',
								top: '0',
								width: '100%',
								height: 'calc(100% + 1rem)',
								background: 'rgba(255, 255, 255, 0.75)',
								backdropFilter: 'blur(4px)',
								zIndex: '-1',
								margin: '-0.5rem 0'
							}
						}}
						aria-label="Teilen"
						title="Teilen"
						position="relative"
						onClick={() => {
							scrollToComments();
						}}
						onKeyDown={({ code }) => {
							if (code === 'Enter') {
								scrollToComments();
							}
						}}
					>
						<Icon
							width="1.5rem"
							height="1.5rem"
							fontSize={'1.125rem'}
							color={themeContext.sharingMenu.trigger.button.iconColor}
							code={Icons.commentOutline}
							marginRight="0.38rem"
						/>
						<Box
							color={themeContext.sharingMenu.trigger.button.textColor}
							textTransform="uppercase"
							fontSize="0.6875rem"
							fontStyle="normal"
							fontWeight="700"
							lineHeight="0.6875rem"
							letterSpacing="0.08313rem"
						>
							Kommentare
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default SharingMenuArticle;
